import { createApp } from "vue";
import App from "./App.vue";
import axios from "axios";
import VueAxios from "vue-axios";
import VueCookies from "vue3-cookies";
import { router } from "@/router/index.js";
import { i18n } from "@/locale/i18n";
import { toHtmlLang } from "@/util/htmlLang.mjs";
import global from "@/plugin/global";
import store from "@/store/index.js";
import { $router, $axios } from "dot-framework";
import VueGtag from "vue-gtag";
import CKEditor from "@ckeditor/ckeditor5-vue";
import filter from "@/plugin/filter"
import mixins from "@/plugin/mixins"
import { configureFabricCompatibility } from "@/util/canvas/fabricCompatibility"
import { announce } from "@/util/liveAnnouncer"
import {clearSkippedFirmwareVersions} from "@/util/firmwareUpdatePreference"

/**
 * Bootstrap 설정
 */
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.bundle.js"
import 'bootstrap-icons/font/bootstrap-icons.css';
// 컴포넌트가 쓰는 것과 같은 Modal(ESM). 위 bundle.js 와는 다른 사본이라 그 인스턴스를 찾지 못한다.
import {Modal} from "bootstrap";
import {installFocusTrapRearm} from "@/util/bootstrapModalA11y";
/**
 * SweetAlert2 설정
 */
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

// ---------------------------------
// app 선언
// ---------------------------------
configureFabricCompatibility()
// 모달 위 모달을 닫으면 아래 모달의 초점 가두기를 다시 켠다(ACCESSIBILITY.md 4).
installFocusTrapRearm(el => Modal.getInstance(el))
const app = createApp(App);
app.use(i18n);                // 다국어 사용
app.use(store);               // vuex 사용
app.use(router);              // 라우터 사용
app.use(VueAxios, axios);     // axios 사용
app.use(VueCookies);          // 쿠키 사용
if (process.env.NODE_ENV === 'development') {
  window.clearDotPadFirmwareSkipCookies = (deviceModel) =>
      clearSkippedFirmwareVersions(app.config.globalProperties.$cookies, deviceModel);
}
app.use(VueSweetalert2);      // SweetAlert2 사용
app.use(CKEditor);            // CKEditor 사용

// 토스트 팝업창 템플릿
// options 로 표시 시간과 배경 차단 여부를 조절할 수 있다.
// 입력 중에 뜨는 안내처럼 흐름을 끊으면 안 되는 경우 짧게 띄우고 배경을 막지 않는다.
//
// 버튼이 없는 안내(저장 완료·오류 등)는 토스트로 띄운다. 예전에는 가운데 모달로 띄워 포커스를 가져간 뒤
// 3초 만에 닫혀, 스크린리더 사용자는 다 듣기 전에 사라지고 포커스도 흔들렸다. 읽기는 토스트가 아니라
// 공용 안내기(liveAnnouncer)가 한 번만 맡는다(간편모드 캔버스 안내와 같은 방식).
// 확인·보조 버튼이 있는 경우만 예전처럼 모달(또는 요청한 토스트)로 둔다.
const silenceToast = popup => {
    popup.setAttribute('role', 'presentation');
    popup.setAttribute('aria-live', 'off');
    popup.removeAttribute('aria-modal');
    popup.querySelector('.swal2-html-container')?.setAttribute('aria-hidden', 'true');
};

app.config.globalProperties.$showToast = function (title, message = '', type = 'success', options = {}) {
    const interactive = Boolean(options.actionText || options.secondaryActionText);
    const isToast = options.toast ?? !interactive;
    const swal = this.$swal;
    const lang = toHtmlLang(this.$i18n?.locale);
    const iconClass = type === 'success' ? 'bi-check2' : type === 'error' ? 'bi-x' : 'bi-info';
    const iconColor = type === 'success' ? '#ea5414' : type === 'error' ? '#dc3545' : '#0d6efd';
    const srContextText = type === 'success' ? `${i18n.global.t('성공')}: ` : type === 'error' ? `${i18n.global.t('오류')}: ` : `${i18n.global.t('알림')}: `;
    // 안내기는 토스트가 생기기 전에 부른다(읽을 자리를 지금 열린 대화상자로 고르므로).
    // 문장 안의 HTML 은 화면용이라 글자만 읽는다.
    if (isToast) {
        const plain = value => {
            const element = document.createElement('div');
            element.innerHTML = String(value ?? '');
            return element.textContent.trim();
        };
        const spoken = [plain(title), plain(message)].filter(Boolean).join('. ');
        if (spoken) announce(srContextText + spoken, {politeness: type === 'error' ? 'assertive' : 'polite'});
    }
    // 기존 확인창의 HTML 메시지는 유지하고, 토스트 안내는 일반 텍스트로 표시한다.
    const renderText = value => {
        if (!isToast) return value;
        const element = document.createElement('span');
        element.textContent = value;
        return element.innerHTML;
    };
    let previousFocus = document.activeElement;
    let focusWasInPopup = false;
    let cleanup = () => {};

    // actionText는 확인, secondaryActionText는 보조 버튼이며 각각 isConfirmed/isDenied로 구분한다.
    return swal({
        toast: isToast,
        backdrop: options.backdrop ?? true,
        // 토스트는 아래 가운데가 기본이다. 가리면 안 되는 단추가 아래에 있는 창은 options.position 으로 옮긴다.
        position: isToast ? (options.position ?? 'bottom') : 'center',
        ...(isToast ? {width: 'max-content'} : {}),
        showConfirmButton: !isToast && Boolean(options.actionText),
        confirmButtonText: options.actionText ?? '',
        showDenyButton: Boolean(options.secondaryActionText),
        denyButtonText: options.secondaryActionText ?? '',
        showCloseButton: isToast,
        closeButtonAriaLabel: this.$t('닫기'),
        timer: options.timer ?? (isToast ? 10000 : 3000),
        timerProgressBar: isToast,
        background: '#ffffff',
        html: `
          <div class="modern-toast-content" ${isToast ? '' : 'role="alert" aria-live="assertive"'}>
              <div class="modern-toast-heading ${message ? 'modern-toast-heading--with-detail' : ''}">
                  <div class="modern-toast-icon" aria-hidden="true" style="background-color: ${iconColor};">
                      <i class="bi ${iconClass}"></i>
                  </div>
                  <div class="modern-toast-title">
                      ${renderText(title)}
                  </div>
              </div>
              ${isToast ? '' : `<span class="visually-hidden">${srContextText}</span>`}
              ${message ? `
                  <div class="modern-toast-detail">
                      ${renderText(message)}
                  </div>
              ` : ''}
          </div>
        `,
        customClass: {
            container: isToast ? 'modern-toast-container' : '',
            popup: 'modern-toast-popup' + (isToast ? ' modern-toast-popup--toast'
                : options.secondaryActionText ? ' modern-toast-popup--with-secondary-action' : '')
        },
        // SweetAlert2 는 토스트를 role=alert 로 만든다. didOpen 은 setTimeout 뒤라 그때 바꾸면 이미 한 번
        // 읽힌 뒤다. 안내기가 읽으므로 같은 작업 안에서(willOpen) 토스트 글자를 접근성 트리에서 뺀다.
        // 닫기·보조 버튼은 그대로 남아 Tab 으로 갈 수 있다.
        willOpen(popup) {
            if (!isToast) return;
            silenceToast(popup);
        },
        didOpen(popup) {
            if (!isToast) return;
            silenceToast(popup);
            popup.setAttribute('dir', 'auto');
            if (lang) popup.setAttribute('lang', lang);

            let hovered = popup.matches(':hover');
            const updateTimer = () => {
                if (swal.getPopup() !== popup) return;
                if (hovered || popup.contains(document.activeElement) || document.hidden) swal.stopTimer();
                else swal.resumeTimer();
            };
            const onEnter = () => { hovered = true; updateTimer(); };
            const onLeave = () => { hovered = false; updateTimer(); };
            const onFocusIn = event => {
                focusWasInPopup = true;
                if (event.relatedTarget && event.relatedTarget !== document.body && !popup.contains(event.relatedTarget)) {
                    previousFocus = event.relatedTarget;
                }
                updateTimer();
            };
            const onFocusOut = event => {
                // SweetAlert disables an action before closing, which can blur it to the body.
                focusWasInPopup = popup.contains(event.relatedTarget) || (!event.relatedTarget && event.target.disabled);
                queueMicrotask(updateTimer);
            };
            const onKeyDown = event => {
                event.stopPropagation();
                if (event.key === 'Escape') swal.close({isDismissed: true, dismiss: 'esc'});
            };
            popup.addEventListener('pointerenter', onEnter);
            popup.addEventListener('pointerleave', onLeave);
            popup.addEventListener('focusin', onFocusIn);
            popup.addEventListener('focusout', onFocusOut);
            popup.addEventListener('keydown', onKeyDown);
            document.addEventListener('visibilitychange', updateTimer);
            cleanup = () => {
                popup.removeEventListener('pointerenter', onEnter);
                popup.removeEventListener('pointerleave', onLeave);
                popup.removeEventListener('focusin', onFocusIn);
                popup.removeEventListener('focusout', onFocusOut);
                popup.removeEventListener('keydown', onKeyDown);
                document.removeEventListener('visibilitychange', updateTimer);
            };
            updateTimer();
        },
        willClose(popup) {
            if (!isToast) return;
            cleanup();
            const restoreFocus = popup.contains(document.activeElement) || (focusWasInPopup && document.activeElement === document.body);
            if (restoreFocus && previousFocus?.isConnected) previousFocus.focus({preventScroll: true});
        },
        didDestroy() { cleanup(); },
    });
};

if (process.env.VUE_APP_MODE === "real") {
  app.use(VueGtag, {    // Google Analytics 사용
    config: {
      id: "G-GKWZ7NS7BZ"
    }
  }, router);
}
app.config.devtools = true
app.config.globalProperties.$filter = filter
app.config.globalProperties.$mixins = mixins
app.mount("#app");

// ---------------------------------
// 라우트 이동처리 및 axios 인터셉터 처리
// ---------------------------------
$router.setBeforeEach(router);
$axios.interceptorsRequest();
$axios.interceptorsResponse();

// ---------------------------------
// 통합인증 및 메뉴/권한 설정
// ---------------------------------
global.setSession();
